<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UploadBillingListItems',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onSumbitCompleted = (...args) => {
    const [event] = args;
    const data = event.data.response.data;

    if (data == null) {
      resultText.value = messages.value.noRecordsHaveBeenImported;
      return;
    }

    const items = data.result ? data.result.length : 0;

    resultText.value = $fn.value.sprintf(
      messages.value.recordsHaveBeenImported,
      { items }
    );
  };

  const file = ref(null);
  const formId = ref(null);
  const formName = ref('uploadBillingListItems');
  const resultText = ref(null);
  const templateFileURL = ref(null);
  const messages = ref({
    noRecordsHaveBeenImported: $fn.value.pgettext(
      'UploadBillingListItems',
      'No records have been imported.'
    ),
    recordsHaveBeenImported: $fn.value.pgettext(
      'UploadBillingListItems',
      '%(items)s items have been imported.'
    ),
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_3086171180629160_validation = computed(() => [
    'ext:xlsx',
    'required',
  ]);

  const $data = computed(() => ({
    file: file.value,
    formId: formId.value,
    formName: formName.value,
    resultText: resultText.value,
    templateFileURL: templateFileURL.value,
    messages: messages.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3086171180629160_validation: b_3086171180629160_validation.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_templateFileURL = () => {
    return $fn.value.urlFor('get_billing_list_items_upload_template', {
      format: 'xlsx',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'file' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'resultText' },
      { name: 'templateFileURL' },
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3086171180629160_validation', nodeId: '3086171180629160' },
    ],
    loaders: { formId: $$load_formId, templateFileURL: $$load_templateFileURL },
    ux: {},
    dataRefs: {
      file,
      formId,
      formName,
      resultText,
      templateFileURL,
      messages,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3086171180629160_validation,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'UploadBillingListItems|Title',
        'Upload Billing List Items'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_wizsexmjh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_648747096704084_script_kzkryjs4e_value_callable = async (
    $event
  ) => {
    return onSumbitCompleted($event);
  };
  const act_648747096704084_script_kzkryjs4e_getActionArgs = async ($event) => {
    return {
      value: async () => {
        await act_648747096704084_script_kzkryjs4e_value_callable($event);
      },
    };
  };
  const act_648747096704084_request_r46jnrz45_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'upload_billing_list_items',
      data: {
        file: file.value,
      },
      contentType: 'multipart/form-data',
    };
  };
  const act_2434514588044777_closeModal_sa3c6k4wq_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_wizsexmjh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_wizsexmjh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ckz0zs76s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_wizsexmjh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ckz0zs76s],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_1xar1x1lg =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4zeo61okp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1xar1x1lg,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4zeo61okp],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_5hudfi5r5 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_0ts45ydru =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_5hudfi5r5,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_0ts45ydru],
    event: 'close',
  });
  const act_648747096704084_script_kzkryjs4e = new actionTypes.ScriptAction({
    actionArgs: act_648747096704084_script_kzkryjs4e_getActionArgs,
  });
  const inst_evh_648747096704084_success_uvyifkw18 =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_script_kzkryjs4e,
      event: 'success',
      displayName: 'script',
    });
  const act_648747096704084_request_r46jnrz45 = new actionTypes.RequestAction({
    actionArgs: act_648747096704084_request_r46jnrz45_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_648747096704084_success_uvyifkw18],
  });
  const inst_evh_648747096704084_submit_cs55hxijx =
    new actionTypes.EventHandlerImpl({
      action: act_648747096704084_request_r46jnrz45,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_648747096704084_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_648747096704084_submit_cs55hxijx],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_sa3c6k4wq =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_sa3c6k4wq_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_bybr8pt81 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_sa3c6k4wq,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_bybr8pt81],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ckz0zs76s,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4zeo61okp,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_0ts45ydru,
    inst_evh_8564662037462133_close,
    inst_evh_648747096704084_success_uvyifkw18,
    inst_evh_648747096704084_submit_cs55hxijx,
    submit: inst_evh_648747096704084_submit_cs55hxijx,
    inst_evh_648747096704084_submit,
    inst_evh_2434514588044777_clickPreventStop_bybr8pt81,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_ckz0zs76s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ckz0zs76s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4zeo61okp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4zeo61okp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_0ts45ydru = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_0ts45ydru.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_success_uvyifkw18 = async (event, scope) => {
    return await actions.inst_evh_648747096704084_success_uvyifkw18.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit_cs55hxijx = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit_cs55hxijx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_648747096704084_submit = async (event, scope) => {
    return await actions.inst_evh_648747096704084_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_bybr8pt81 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_bybr8pt81.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="UploadBillingListItems"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext('UploadBillingListItems|ResourceTitle', 'Billing Plan')
        "
        :resourceKey="
          pgettext('UploadBillingListItems|ResourceTitle', 'Upload Items')
        "
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <ItpIcon fa="info-circle"> </ItpIcon>
        <ItpText wrap>
          {{
            pgettext(
              'UploadBillingListItems',
              'Upload billing list items from a template file.'
            )
          }}
        </ItpText>
      </ItpAlert>
      <hr />
      <div class="container">
        <ItpForm
          :name="formName"
          :id="formId"
          v-if="loaded"
          @submit="evh_648747096704084_submit($event, {})"
        >
          <ItpFormGroup
            name="file"
            :label="pgettext('UploadBillingListItems', 'Input file')"
            labelFor="file"
            required
          >
            <ItpFormFile
              v-model="file"
              name="file"
              accept=".xlsx"
              required
              :validation="b_3086171180629160_validation"
            >
            </ItpFormFile>
            <template #description>
              <div class="pt-2">
                <span>
                  {{
                    pgettext(
                      'UploadBillingListItems',
                      'Only XLSX files are allowed.'
                    )
                  }}
                </span>
                <ItpLink
                  :text="
                    pgettext('UploadBillingListItems', 'Download template')
                  "
                  :href="templateFileURL"
                  use-auth
                  force-download
                  downloadFileName="billing-list-items.xlsx"
                >
                </ItpLink>
              </div>
            </template>
          </ItpFormGroup>
        </ItpForm>
        <ItpAlert :show="resultText && !actions.submit.isRunning" class="mt-3">
          <ItpIcon fa="info-circle"> </ItpIcon>
          <span>
            {{ resultText }}
          </span>
        </ItpAlert>
      </div>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="formId"
          :text="pgettext('UploadBillingListItems', 'Upload')"
          type="submit"
          variant="primary"
          :icon="$config.uploadIcon"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
